import { useState, useEffect, useCallback } from "react";
import { MeetingRoom } from "@/components/meeting/MeetingRoom";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Video, Mic, MicOff, VideoOff, PhoneOff, MonitorUp, Users, Plus, Copy, UserPlus, Send, Calendar, Clock, Play, MessageSquare, Upload, Archive, Trash2, Share2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { MeetingSetupFields } from "@/components/meeting/MeetingSetupFields";

const SA_LOGO = "https://customer-assets.emergentagent.com/job_shop-advisor-demo/artifacts/qy6rjh9d_Dynamic%20Logo%20for%20Automotive%20Channel%20Remix%20the%20Whip.png";

const DefaultSignup = () => {
  const [v, setV] = useState("");
  useEffect(() => { api.get("/api/rtc/settings/guest-signup").then(r => setV(r.data.guest_signup_default)).catch(() => {}); }, []);
  const save = (x) => { setV(x); api.put("/api/rtc/settings/guest-signup", { guest_signup_default: x }).then(() => toast.success("Default saved")).catch(() => toast.error("Couldn't save")); };
  return (
    <label className="mt-2 flex items-center gap-2 text-xs text-slate-500">Default for all meetings:
      <select value={v} onChange={e => save(e.target.value)} className="border border-slate-200 rounded px-2 py-1 text-xs" data-testid="guest-signup-default">
        <option value="customer">Customer account</option><option value="sales_rep">Sales rep / affiliate</option><option value="off">No sign-up</option>
      </select></label>
  );
};


function RoomList({ onJoin }) {
  const { user } = useAuth();
  const realAdmin = user?.role === "admin";
  const isAdmin = realAdmin || (user?.role === "sales_rep" && !!user?.can_host_meetings);
  const [rooms, setRooms] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [thumbnails, setThumbnails] = useState([]);
  const [form, setForm] = useState({ title: "", description: "", scheduled_at: "", scheduled_time: "", duration: 60, thumbnail: "", allow_guest: true, call_type: "", product_id: "shop-advisor" });
  const [inviteOpen, setInviteOpen] = useState(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [people, setPeople] = useState([]);
  useEffect(() => { if (realAdmin) api.get("/api/admin/users").then(r => setPeople(r.data.filter(u => u.role !== "admin"))).catch(() => {}); }, [realAdmin]);
  const [uploading, setUploading] = useState(false);
  const [picked, setPicked] = useState([]);
  const delRooms = async (ids, msg, ended = false) => {
    if (!window.confirm(`${msg} This can't be undone.`)) return;
    try { const { data } = await api.post("/api/conferences/bulk-delete", { ids, ended }); toast.success(data.message); setPicked([]); loadRooms(); }
    catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
  };
  const loadRooms = useCallback(async () => {
    try {
      const [r, t] = await Promise.all([api.get("/api/conferences"), api.get("/api/conference-thumbnails")]);
      setRooms(r.data); setThumbnails(t.data);
    } catch {}
  }, []);
  useEffect(() => { loadRooms(); }, [loadRooms]);

  const createRoom = async () => {
    try {
      const start_at = form.scheduled_at && form.scheduled_time ? new Date(`${form.scheduled_at}T${form.scheduled_time}`).toISOString() : null;
      const { data } = await api.post("/api/conferences", { ...form, start_at });
      toast.success(`Meeting created! Room: ${data.room_code} | Guest: ${data.guest_code}`);
      setCreateOpen(false);
      setForm({ title: "", description: "", scheduled_at: "", scheduled_time: "", duration: 60, thumbnail: "", allow_guest: true, call_type: "", product_id: "shop-advisor" });
      loadRooms();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const startMeeting = async (id) => {
    try { const { data } = await api.post(`/api/conferences/${id}/start`); toast.success(data.message); loadRooms(); } catch { toast.error("Failed"); }
  };

  const invite = async (confId) => {
    try { await api.post(`/api/conferences/${confId}/invite`, { email: inviteEmail }); toast.success(`Invited ${inviteEmail} — message sent`); setInviteEmail(""); setInviteOpen(null); loadRooms(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const remindNow = async (id) => {
    try { const { data } = await api.post(`/api/conferences/${id}/remind`); toast.success(data.message); if (data.sms_errors?.length) toast.warning(`Texts not sent: ${data.sms_errors.join("; ")}`); loadRooms(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const copyInvite = (room) => {
    const text = room.invite_message || `Meeting: ${room.title}\nRoom Code: ${room.room_code}\nGuest Code: ${room.guest_code || "N/A"}\nVisit www.theshopadvisor.com`;
    const link = room.allow_guest && room.guest_code ? `\n\nJoin link: ${window.location.origin}/meet/${room.guest_code}` : "";
    navigator.clipboard.writeText(text + link);
    toast.success("Invite copied to clipboard!");
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Max 10MB"); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post("/api/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
      const fileUrl = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      setForm(prev => ({ ...prev, thumbnail: fileUrl }));
      toast.success("Image uploaded!");
    } catch (err) { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 pt-20 pb-12 px-4 md:px-8" data-testid="conference-list">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Conference Room</h1>
            <p className="text-slate-500 text-sm">{isAdmin ? "Create and manage meetings for onboarding and collaboration." : "Meetings you've been invited to."}</p>
          </div>
          {isAdmin && (
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <button className="bg-blue-600 hover:bg-blue-500 text-white px-5 py-2.5 rounded-lg text-sm font-medium flex items-center gap-2" data-testid="create-room-btn"><Plus className="w-4 h-4" /> New Meeting</button>
              </DialogTrigger>
              <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle>Create Meeting</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <div><Label className="text-sm">Title *</Label><Input className="border-slate-300" placeholder="Onboarding Call" value={form.title} onChange={e => setForm({...form, title: e.target.value})} data-testid="room-title-input" /></div>
                  <MeetingSetupFields form={form} setForm={setForm} isAdmin={realAdmin} />
                  <div className="grid grid-cols-3 gap-3">
                    <div><Label className="text-sm">Date</Label><Input type="date" className="border-slate-300" value={form.scheduled_at} onChange={e => setForm({...form, scheduled_at: e.target.value})} /></div>
                    <div><Label className="text-sm">Time</Label><Input type="time" className="border-slate-300" value={form.scheduled_time} onChange={e => setForm({...form, scheduled_time: e.target.value})} /></div>
                    <div><Label className="text-sm">Duration (min)</Label><Input type="number" className="border-slate-300" value={form.duration} onChange={e => setForm({...form, duration: parseInt(e.target.value) || 60})} /></div>
                  </div>
                  <div>
                    <Label className="text-sm mb-2 block">Thumbnail — select a logo or upload from device</Label>
                    <div className="grid grid-cols-4 gap-2 mb-3">
                      {thumbnails.map((t, i) => (
                        <button key={i} type="button" onClick={() => setForm({...form, thumbnail: t.url})}
                          className={`border rounded-lg p-2 h-16 flex items-center justify-center overflow-hidden transition-all ${form.thumbnail === t.url ? "border-blue-500 bg-blue-50 ring-2 ring-blue-200" : "border-slate-200 hover:border-blue-300"}`}>
                          <div className="overflow-hidden w-full h-full flex items-center justify-center">
                            <img src={t.url} alt={t.name} className="w-full h-auto" style={{ marginTop: "-30%", marginBottom: "-30%" }} />
                          </div>
                        </button>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <label className={`flex-1 border border-dashed border-slate-300 rounded-lg p-3 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors ${uploading ? "opacity-50 pointer-events-none" : ""}`}>
                        <Upload className="w-5 h-5 mx-auto mb-1 text-slate-400" />
                        <span className="text-xs text-slate-500">{uploading ? "Uploading..." : "Upload from device"}</span>
                        <input type="file" accept="image/*,video/*" className="hidden" onChange={handleImageUpload} />
                      </label>
                    </div>
                    <Input className="border-slate-300 mt-2" placeholder="Or paste image URL" value={form.thumbnail} onChange={e => setForm({...form, thumbnail: e.target.value})} />
                    {form.thumbnail && <img src={form.thumbnail} alt="Preview" className="w-full h-20 object-cover rounded-lg mt-2 border border-slate-200" />}
                  </div>
                  <div className="flex items-center gap-2">
                    <input type="checkbox" checked={form.allow_guest} onChange={e => setForm({...form, allow_guest: e.target.checked})} className="rounded" />
                    <Label className="text-sm">Allow guest entry with code</Label>
                  </div>
                  {form.allow_guest && <div>
                    <Label className="text-sm">Guests must sign up first</Label>
                    <select value={form.guest_signup || ""} onChange={e => setForm({ ...form, guest_signup: e.target.value })} className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="room-guest-signup">
                      <option value="">Use the default setting</option>
                      <option value="customer">Yes, as a customer (portal account)</option>
                      <option value="sales_rep">Yes, as a sales rep or affiliate (application)</option>
                      <option value="off">No, just their name</option>
                    </select>
                    {realAdmin && <DefaultSignup />}
                  </div>}
                  <button onClick={createRoom} className="w-full bg-blue-600 hover:bg-blue-500 text-white font-bold py-2.5 rounded-lg" data-testid="room-create-confirm-btn">Create Meeting</button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {isAdmin && rooms.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mb-3 text-sm" data-testid="room-cleanup-bar">
            {picked.length > 0 && <button onClick={() => delRooms(picked, `Delete ${picked.length} selected meeting(s)?`)} className="bg-red-600 text-white px-3 py-1.5 rounded-lg flex items-center gap-1" data-testid="delete-selected-rooms"><Trash2 className="w-3.5 h-3.5" />Delete {picked.length} selected</button>}
            {rooms.some(r => r.status === "ended") && <button onClick={() => delRooms([], `Delete all ${rooms.filter(r => r.status === "ended").length} ended meetings?`, true)} className="border border-red-200 text-red-600 px-3 py-1.5 rounded-lg flex items-center gap-1" data-testid="delete-ended-rooms"><Trash2 className="w-3.5 h-3.5" />Delete all ended</button>}
            <button onClick={() => setPicked(picked.length === rooms.length ? [] : rooms.map(r => r.id))} className="text-slate-500 underline" data-testid="select-all-rooms">{picked.length === rooms.length ? "Clear selection" : "Select all"}</button>
            <span className="text-xs text-slate-400">Recordings stay in Admin › Recordings.</span>
          </div>
        )}
        <div className="grid gap-4">
          {rooms.map(room => (
            <div key={room.id} className={`bg-white border rounded-xl overflow-hidden shadow-sm ${room.status === "active" ? "border-green-300" : room.status === "scheduled" ? "border-blue-300" : "border-slate-200"}`} data-testid={`room-card-${room.id}`}>
              <div className="flex">
                {/* Thumbnail */}
                {room.thumbnail && (
                  <div className="w-28 sm:w-36 shrink-0 bg-slate-100 overflow-hidden flex items-center justify-center">
                    <img src={room.thumbnail} alt="" className="w-full h-auto" style={{ marginTop: "-30%", marginBottom: "-30%" }} />
                  </div>
                )}
                <div className="flex-1 p-4 sm:p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-slate-900 font-semibold">{room.title}</h3>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${room.status === "active" ? "bg-green-50 text-green-600" : room.status === "scheduled" ? "bg-blue-50 text-blue-600" : "bg-slate-100 text-slate-500"}`}>{room.status}</span>
                      </div>
                      {room.description && <p className="text-slate-500 text-xs mb-2">{room.description}</p>}
                      <div className="flex flex-wrap gap-3 text-xs text-slate-500">
                        <span>Host: {room.host_name}</span>
                        <span className="flex items-center gap-1"><Users className="w-3 h-3" />{(room.participants || []).length}</span>
                        {room.scheduled_at && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{room.scheduled_at}</span>}
                        {room.scheduled_time && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{room.scheduled_time}</span>}
                        {room.duration && <span>{room.duration}m</span>}
                        {isAdmin && room.status === "scheduled" && (room.reminder_sent_at ? <span className="text-green-600" data-testid={`reminder-sent-${room.id}`}>Reminder sent</span> : room.start_at && <span className="text-blue-600" data-testid={`reminder-auto-${room.id}`}>Auto-reminder 15 min before</span>)}
                      </div>
                      <div className="flex gap-2 mt-2">
                        <span className="text-[10px] bg-slate-100 px-2 py-0.5 rounded font-mono">Room: {room.room_code}</span>
                        {room.guest_code && <span className="text-[10px] bg-yellow-50 text-yellow-700 px-2 py-0.5 rounded font-mono">Guest: {room.guest_code}</span>}
                      </div>
                    </div>
                    <div className="flex flex-col gap-1.5 shrink-0">
                      {room.status === "active" && (
                        <button onClick={() => onJoin(room.id)} className="bg-green-600 hover:bg-green-500 text-white px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1" data-testid={`join-room-${room.id}`}><Video className="w-3.5 h-3.5" /> Join</button>
                      )}
                      {isAdmin && room.status === "scheduled" && (
                        <button onClick={async () => { await startMeeting(room.id); onJoin(room.id); }} className="bg-blue-600 hover:bg-blue-500 text-white px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1" data-testid={`start-room-${room.id}`}><Play className="w-3.5 h-3.5" /> Start & join</button>
                      )}
                      {isAdmin && room.status === "ended" && (
                        <button onClick={async () => { await startMeeting(room.id); onJoin(room.id); }} className="bg-amber-500 hover:bg-amber-400 text-white px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1" data-testid={`restart-room-${room.id}`}><RotateCcw className="w-3.5 h-3.5" /> Restart & join</button>
                      )}
                      {isAdmin && (
                        <button onClick={() => setInviteOpen(room.id)} className="bg-slate-100 hover:bg-slate-200 text-slate-600 px-3 py-1.5 rounded-lg text-xs flex items-center gap-1" data-testid={`invite-room-${room.id}`}><UserPlus className="w-3.5 h-3.5" /> Invite</button>
                      )}
                      {isAdmin && room.status === "scheduled" && (
                        <button onClick={() => remindNow(room.id)} className="bg-slate-100 hover:bg-slate-200 text-slate-600 px-3 py-1.5 rounded-lg text-xs flex items-center gap-1" data-testid={`remind-room-${room.id}`}><Send className="w-3.5 h-3.5" /> Remind now</button>
                      )}
                      <button onClick={() => copyInvite(room)} className="bg-slate-100 hover:bg-slate-200 text-slate-600 px-3 py-1.5 rounded-lg text-xs flex items-center gap-1" data-testid={`copy-invite-${room.id}`}><Copy className="w-3.5 h-3.5" /> Copy</button>
                      {isAdmin && (
                        <button onClick={() => delRooms([room.id], `Delete "${room.title}"?`)} className="bg-red-50 hover:bg-red-100 text-red-600 px-3 py-1.5 rounded-lg text-xs flex items-center gap-1" data-testid={`delete-room-${room.id}`}><Trash2 className="w-3.5 h-3.5" /> Delete</button>
                      )}
                      {isAdmin && <label className="text-[11px] text-slate-500 flex items-center gap-1 justify-center"><input type="checkbox" checked={picked.includes(room.id)} onChange={e => setPicked(e.target.checked ? [...picked, room.id] : picked.filter(x => x !== room.id))} data-testid={`pick-room-${room.id}`} />Select</label>}
                    </div>
                  </div>
                  {/* Inline invite */}
                  {inviteOpen === room.id && (
                    <div className="flex gap-2 mt-3 pt-3 border-t border-slate-100">
                      <select value="" onChange={e => e.target.value && setInviteEmail(e.target.value)} className="h-9 rounded-md border border-slate-300 bg-white px-2 text-sm max-w-[45%]" data-testid="invite-customer-select">
                        <option value="">Pick a customer…</option>
                        {people.map(u => <option key={u.id} value={u.email}>{u.name} ({u.email})</option>)}
                      </select>
                      <Input className="border-slate-300 flex-1 text-sm" placeholder="or type any email" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} data-testid="invite-email-input" />
                      <button onClick={() => invite(room.id)} className="bg-blue-600 text-white px-3 py-1.5 rounded-lg text-xs font-medium" data-testid="invite-send-btn">Send Invite</button>
                      <button onClick={() => setInviteOpen(null)} className="text-slate-400 text-xs px-2">Cancel</button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
          {rooms.length === 0 && (
            <div className="text-center py-16 text-slate-400"><Video className="w-12 h-12 mx-auto mb-3 opacity-40" /><p>{isAdmin ? "No meetings yet. Create your first meeting." : "No meetings scheduled. Admin will invite you."}</p></div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ConferencePage() {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const [activeRoom, setActiveRoom] = useState(roomId || null);
  if (activeRoom) return <MeetingRoom confId={activeRoom} joinBody={{}} onLeave={() => { setActiveRoom(null); navigate("/conference"); }} />;
  return <RoomList onJoin={setActiveRoom} />;
}
